/* eslint-disable react/prop-types */
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@egaranti/components";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Check, ChevronDown, Loader2, Plus } from "lucide-react";

/**
 * EPS-4970 revize — "Satış Ekle" akışının arama kutuları.
 *
 * `CategoryCombobox` id ile çalışıyor; burada buna uyan bir alan yok:
 * - **Marka**: merchant'ın marka listesi uçtan yalnızca AD olarak dönüyor
 *   (`/merchant-products/auto-complete/brands` distinct isim döner, id null) ve
 *   product-api de markayı ad olarak alıyor.
 * - **Ürün Adı**: mevcut ürün seçilirse id'si de gerekiyor (o ürünün kendi kategori
 *   zinciriyle plan eşleştirmesi daha isabetli oluyor), yeni ad yazılırsa id yok.
 *
 * Bu yüzden seçim sonucu tek bir string değil: `onSelect(item)` mevcut kaydı,
 * `onCreate(name)` ise yeni adı bildirir. Görünen değer her zaman `value` (ad).
 * Arama uzaktan yapılır (`onSearch`), yazma sırasında 300 ms geciktirilir.
 */
const SEARCH_DEBOUNCE_MS = 300;

// Yerel aramada "sahin" yazınca "ŞAHİN"i de bulmak için: Türkçe küçük harf +
// aksan sadeleştirme. ("I" tr'de "ı"ya düşer, birleşik aksanlar NFD ile ayrılıp
// atılır.)
const normalize = (text) =>
  (text || "")
    .toLocaleLowerCase("tr")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i");

const SearchSelect = ({
  value = "",
  items = [],
  onSelect,
  onCreate,
  onSearch,
  placeholder = "",
  disabled = false,
  loading = false,
  allowCreate = false,
  emptyHint,
  ariaLabel,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debounceRef = useRef(null);

  // Uzak arama: her tuş vuruşunda istek atmamak için geciktiriyoruz. Kapanışta
  // arama sıfırlanır ki liste bir sonraki açılışta baştan yüklensin.
  useEffect(() => {
    if (!open || !onSearch) return undefined;
    debounceRef.current = setTimeout(() => onSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [search, open, onSearch]);

  const trimmed = search.trim();

  // `Command`'ın kendi filtresi kapalı (shouldFilter={false}) çünkü uzak aramada
  // listeyi BE süzüyor. `onSearch` verilmeyen alanlarda (ör. Kategori) liste tam
  // geldiği için süzmeyi burada yapmak zorundayız — aksi hâlde yazmak hiçbir şey
  // yapmaz.
  const visibleItems = useMemo(() => {
    if (onSearch || !trimmed) return items;
    const query = normalize(trimmed);
    return items.filter(
      (item) =>
        normalize(item.name).includes(query) ||
        normalize(item.description).includes(query),
    );
  }, [items, onSearch, trimmed]);

  const exactExists = items.some(
    (item) => item.name?.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  const showCreateRow = allowCreate && trimmed.length > 0 && !exactExists;

  const close = () => {
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        {/* Sınıflar SelectTrigger / CategoryCombobox ile birebir — form içinde
            alan yükseklikleri kaymasın. */}
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel || placeholder}
          disabled={disabled}
          className="ring-offset-background focus:ring-ring flex w-full items-center justify-between rounded-lg border border-[#CDD5E0] p-3 text-left text-[#111729] focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className={value ? "line-clamp-1" : "text-[#677389]"}>
            {value || placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] min-w-[280px] p-0"
      >
        <Command shouldFilter={false} className="overflow-visible">
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder={placeholder}
          />
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("common.loading", "Yükleniyor...")}
            </div>
          ) : (
            <>
              {visibleItems.length === 0 && !showCreateRow && (
                <CommandEmpty>
                  {emptyHint || t("common.noResults", "Sonuç bulunamadı")}
                </CommandEmpty>
              )}
              <CommandGroup className="max-h-60 overflow-auto">
                {visibleItems.map((item) => (
                  <CommandItem
                    key={item.key}
                    value={String(item.key)}
                    onSelect={() => {
                      onSelect?.(item);
                      close();
                    }}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 shrink-0 ${
                        value === item.name ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <span className="flex flex-col">
                      <span>{item.name}</span>
                      {item.description && (
                        <span className="text-xs text-gray-500">
                          {item.description}
                        </span>
                      )}
                    </span>
                  </CommandItem>
                ))}
                {showCreateRow && (
                  <CommandItem
                    key="__create__"
                    value={`__create__${trimmed}`}
                    onSelect={() => {
                      onCreate?.(trimmed);
                      close();
                    }}
                    className="text-[#0049E6]"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t("productsPage.addNamed", '"{{name}}" ekle', {
                      name: trimmed,
                    })}
                  </CommandItem>
                )}
              </CommandGroup>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchSelect;
